// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/useAskV2Transport.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/createAskV2Transport.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mapServerMessages.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mergeDisplayMessages.test.ts
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import {
  type AskPunttChat,
  type AskPunttMessage,
  type GetAskPunttChatHistoryResponse,
} from "@mg/schemas/src/christo/askPuntt";
import { type Query } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { applyStreamSnapshot, streamMessageIdFor } from "./applyStreamSnapshot";
import {
  createAskV2Transport,
  type AskV2Transport,
} from "./createAskV2Transport";
import { mapServerMessages, nextAskV2HistoryPollMs } from "./mapServerMessages";
import { mergeDisplayMessages } from "./mergeDisplayMessages";
import { type AskV2TransportSnapshot } from "./types";

import {
  type StreamingState,
  useAskPunttStream,
} from "../../hooks/useAskPunttStream";
import { useAskPunttChatHistory } from "../../utils/queries/askPuntt";
import { generateUUID } from "../../utils/uuid";
import {
  abortAskV2Message,
  createAskV2Chat,
  regenerateAskV2Message,
  sendAskV2Message,
} from "../services/chat";

export type UseAskV2TransportOptions = {
  chatId?: string | null;
  enabled?: boolean;
  presentation?: "chat" | "feed";
};

export type AskV2TransportApi = AskV2TransportSnapshot & {
  setComposer: AskV2Transport["setComposer"];
  setWebSearch: AskV2Transport["setWebSearch"];
  addQuotedContext: AskV2Transport["addQuotedContext"];
  removeQuotedContext: AskV2Transport["removeQuotedContext"];
  clearQuotedContexts: AskV2Transport["clearQuotedContexts"];
  setCommentContext: AskV2Transport["setCommentContext"];
  removeCommentContext: AskV2Transport["removeCommentContext"];
  clearCommentContexts: AskV2Transport["clearCommentContexts"];
  send: AskV2Transport["send"];
  abort: AskV2Transport["abort"];
  retry: AskV2Transport["retry"];
  beginEdit: AskV2Transport["beginEdit"];
  setEditDraft: AskV2Transport["setEditDraft"];
  cancelEdit: AskV2Transport["cancelEdit"];
  submitEdit: AskV2Transport["submitEdit"];
  historyLoading: boolean;
  historyError: boolean;
  chatTitle?: string;
  historyChat?: AskPunttChat;
  historyMessages: AskPunttMessage[];
  displayMessages: AskPunttMessage[];
  /**
   * The live AskPuntt snapshot the running turn is publishing, or null. It is
   * already APPLIED to `historyMessages`/`displayMessages`; this is the raw
   * payload, so a reader that needs a field the thread does not carry (the
   * provisional `verifiedFindings`) does not have to open a second socket for
   * it.
   */
  streamSnapshot: StreamingState | null;
  /** The snapshot's message id (`w<userMessageId>`), or null when none is pending. */
  streamMessageId: string | null;
};

export function useAskV2Transport(
  options?: UseAskV2TransportOptions,
): AskV2TransportApi {
  const routeChatId = options?.chatId ?? null;
  const presentation = options?.presentation ?? "chat";

  const transportRef = useRef<AskV2Transport | null>(null);
  if (transportRef.current == null) {
    transportRef.current = createAskV2Transport({
      id: generateUUID,
      initialChatId: routeChatId,
      api: {
        createChat: ({
          message,
          attachments,
          quotedContexts,
          commentContexts,
          mentions,
          mentionEmails,
          webSearch,
        }) =>
          createAskV2Chat(
            message,
            attachments,
            quotedContexts,
            commentContexts,
            mentions,
            mentionEmails,
            webSearch,
          ),
        sendMessage: ({
          chatId: activeChatId,
          message,
          attachments,
          quotedContexts,
          commentContexts,
          mentions,
          mentionEmails,
          webSearch,
        }) =>
          sendAskV2Message(
            activeChatId,
            message,
            attachments,
            quotedContexts,
            commentContexts,
            mentions,
            mentionEmails,
            webSearch,
          ),
        abortMessage: ({ chatId: activeChatId, messageId }) =>
          abortAskV2Message(activeChatId, messageId),
        regenerateMessage: ({ chatId: activeChatId, messageId, content }) =>
          regenerateAskV2Message(activeChatId, messageId, content),
      },
    });
  }

  const transport = transportRef.current;
  const [snapshot, setSnapshot] = useState(transport.getSnapshot);
  const lastSyncRef = useRef<{ chatId: string | null; updatedAt: number }>({
    chatId: null,
    updatedAt: 0,
  });
  const pollStartedAtRef = useRef<number | null>(null);
  // Whether the server has said, at any point in THIS poll, that the turn is
  // still running. A later read that could not ask must not undo that and stop
  // the poll at the cap; see `nextAskV2HistoryPollMs`.
  const pendingTurnSeenRef = useRef(false);
  const wasInFlightRef = useRef(false);

  const chatId = routeChatId ?? snapshot.chatId;
  const enabled = options?.enabled !== false && !!chatId;

  useEffect(() => {
    if (!routeChatId) return;
    if (transport.getSnapshot().chatId === routeChatId) return;
    // why: project routes learn sharedChatId after load — bind before history
    // returns so send() posts into the shared chat instead of createChat().
    transport.hydrateFromServer({ chatId: routeChatId, messages: [] });
    lastSyncRef.current = { chatId: null, updatedAt: 0 };
    pollStartedAtRef.current = null;
    pendingTurnSeenRef.current = false;
  }, [routeChatId, transport]);

  const historyQuery = useAskPunttChatHistory(chatId, {
    enabled,
    refetchInterval: (query: Query<GetAskPunttChatHistoryResponse, Error>) => {
      const inFlight = transport.getSnapshot().inFlight;
      if (inFlight && !wasInFlightRef.current) {
        pollStartedAtRef.current = null;
        pendingTurnSeenRef.current = false;
      }
      wasInFlightRef.current = inFlight;
      const next = nextAskV2HistoryPollMs({
        queryStatus: query.state.status,
        messages: query.state.data?.messages ?? [],
        inFlight,
        pollStartedAt: pollStartedAtRef.current,
        now: Date.now(),
        agent: query.state.data?.chat?.agent,
        // Why the poll keeps going past the warhol cap; see the function.
        // `undefined` here means the server did not say, which is the case for
        // every chat but warhol and for a read that could not ask.
        pendingTurn: query.state.data?.pendingTurn,
        pendingTurnSeen: pendingTurnSeenRef.current,
      });
      pollStartedAtRef.current = next.pollStartedAt;
      pendingTurnSeenRef.current = next.pendingTurnSeen;
      return next.interval;
    },
  });

  /**
   * The snapshot a running warhol review publishes to, or null.
   *
   * Only warhol writes one under `w<userMessageId>`, and only while it is
   * reviewing: the tail being the person's own message IS "the answer has not
   * landed". Everything else — rembrandt, a finished review, a non-review chat
   * — subscribes to nothing and pays nothing.
   */
  const rawServerMessages = historyQuery.data?.messages;
  const streamMessageId = useMemo(
    () =>
      historyQuery.data?.chat?.agent === "warhol"
        ? streamMessageIdFor(rawServerMessages)
        : null,
    [historyQuery.data?.chat?.agent, rawServerMessages],
  );

  // The same hook the v1 chat streams on, untouched: one shared socket per tab,
  // a `w`-prefixed key warhol writes, and its own `/stream` poll fallback for a
  // dead socket — which is what keeps the rows moving when the socket is
  // blocked. `expectPushes` is true for the whole life of the subscription
  // because the turn is generating for exactly that long.
  const { streamingState: streamSnapshot } = useAskPunttStream(
    chatId ?? "",
    streamMessageId,
    { enabled: !!chatId && !!streamMessageId, expectPushes: true },
  );

  /**
   * History with the live snapshot laid on the pending turn's row.
   *
   * Everything downstream reads THIS and not the raw response: the rail's check
   * rows and the fan-out chips both hang off the message, so an overlay applied
   * any later would reach neither. The 2s history poll stays the source of
   * truth for the answer; this only adds what has happened so far.
   */
  const serverMessages = useMemo(
    () => applyStreamSnapshot(rawServerMessages ?? [], streamSnapshot),
    [rawServerMessages, streamSnapshot],
  );

  useEffect(() => transport.subscribe(setSnapshot), [transport]);

  useEffect(() => {
    if (!enabled || !chatId) {
      if (lastSyncRef.current.chatId) {
        transport.reset();
        lastSyncRef.current = { chatId: null, updatedAt: 0 };
      }
      return;
    }

    if (!historyQuery.data) return;

    // why: getChatHistory returns a bare cast with no validation, and V1 guards
    // this exact field (`if (!chatData?.messages) return` in ask-puntt/chat.tsx)
    // — a response without `messages` makes mapServerMessages' `.filter` throw
    // inside this effect and unmounts the Ask V2 chat via the error boundary.
    if (!historyQuery.data.messages) return;

    // `serverMessages` is the snapshot-overlaid list, and it is an argument to
    // this effect below: a `stream_update` must re-map the thread, or the live
    // agent steps would reach the rail but never the thread's own rows.
    const mapped = mapServerMessages(serverMessages, presentation);
    if (lastSyncRef.current.chatId !== chatId) {
      // why: hydrateFromServer replaces the thread wholesale. That is right for
      // a chat the transport is not bound to yet, but wrong for the FIRST
      // payload of the chat it is already on: the composer renders while
      // history is still loading, so a turn sent in that window is an
      // optimistic sending/failed row the server has not echoed back yet. A
      // blind hydrate drops it — the bubble vanishes mid-send, the ACK can no
      // longer find its clientId to mark sent, and a failed send loses its
      // retry affordance. syncFromServer keeps that local tail.
      if (transport.getSnapshot().chatId === chatId) {
        transport.syncFromServer(mapped);
      } else {
        transport.hydrateFromServer({ chatId, messages: mapped });
      }
    } else if (historyQuery.dataUpdatedAt !== lastSyncRef.current.updatedAt) {
      transport.syncFromServer(mapped);
    }

    lastSyncRef.current = {
      chatId,
      updatedAt: historyQuery.dataUpdatedAt,
    };
  }, [
    chatId,
    enabled,
    historyQuery.data,
    historyQuery.dataUpdatedAt,
    presentation,
    serverMessages,
    transport,
  ]);

  const displayMessages = useMemo(
    () =>
      mergeDisplayMessages({
        historyMessages: serverMessages,
        transportMessages: snapshot.messages,
        chatId: chatId ?? "",
      }),
    [chatId, serverMessages, snapshot.messages],
  );

  return {
    ...snapshot,
    setComposer: transport.setComposer,
    setWebSearch: transport.setWebSearch,
    addQuotedContext: transport.addQuotedContext,
    removeQuotedContext: transport.removeQuotedContext,
    clearQuotedContexts: transport.clearQuotedContexts,
    setCommentContext: transport.setCommentContext,
    removeCommentContext: transport.removeCommentContext,
    clearCommentContexts: transport.clearCommentContexts,
    send: transport.send,
    abort: transport.abort,
    retry: transport.retry,
    beginEdit: transport.beginEdit,
    setEditDraft: transport.setEditDraft,
    cancelEdit: transport.cancelEdit,
    submitEdit: transport.submitEdit,
    historyLoading: enabled && historyQuery.isLoading,
    historyError: enabled && historyQuery.isError,
    chatTitle: historyQuery.data?.chat?.title,
    historyChat: historyQuery.data?.chat,
    historyMessages: serverMessages,
    displayMessages,
    streamSnapshot: streamSnapshot ?? null,
    streamMessageId,
  };
}
